import React, { useCallback, useEffect, useState } from "react";
import {
  useNavigate
} from "react-router-dom";
import {
  Loader2,
  Plus,
  AlertTriangle,
  CheckCircle2,
  Trash2,
  Layers,
  Filter,
  FileSpreadsheet,
  ChevronRight
} from "lucide-react";
import {
  createBatch,
  getAllBatches,
  getAllTemplates,
  getMarksSourcePreview
} from "../../api/reportcard/reportCardApi";
import {
  createBatchPortal,
  getAllBatchesPortal,
  getAllTemplatesPortal,
  getMarksSourcePreviewPortal
} from "../../api/reportcard/reportCardPortalApi";
import {
  getAllClasses,
  getAllSections
} from "../../api/student/studentApi";
import {
  getAllClassesForPortal,
  getAllSectionsForPortal
} from "../../api/student/studentPortalApi";
import {
  getAllAcademicYears,
  getActiveAcademicYear
} from "../../api/academic/academicYearApi";
import {
  getAllAcademicYearsPortal,
  getActiveAcademicYearPortal
} from "../../api/academic/academicYearPortalApi";
import {
  useStaffAuth
} from "../../context/StaffAuthContext";
import BatchStatusBadge from "../../components/reportcard/BatchStatusBadge";
import {
  pickList,
  resolveYearName,
  toId
} from "../../utils/reportcard/reportCardHelpers";
import * as UI from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const Card =
  UI.Card ||
  (({ className, children, ...props }) => (
    <div className={`bg-white dark:bg-neutral-900 border border-primary-100 rounded-2xl shadow-xs ${className}`} {...props}>
      {children}
    </div>
  ));

const Button =
  UI.Button ||
  (({ children, className, ...props }) => (
    <button
      className={`inline-flex items-center justify-center px-3.5 py-2 text-xs font-semibold rounded-xl transition-all cursor-pointer ${className}`}
      {...props}
    >
      {children}
    </button>
  ));

const Badge =
  UI.Badge ||
  (({ children, className }) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${className}`}>
      {children}
    </span>
  ));

const emptyColumn = () => ({
  label: "",
  fullMarks: "20",
  table: "MAJOR",
  optional: false,
  sources: [{ sourceType: "EXAM", examId: "", standaloneSchemeId: "", weightPercent: "100" }],
});

const BatchList = ({ isPortal = false }) => {
  const navigate = useNavigate();
  const staffAuth = useStaffAuth();
  const canManage = isPortal
    ? staffAuth?.hasPermission?.("REPORT_CARD_FULL")
    : true;
  const base = isPortal ? "/staff-panel/report-cards" : "/report-cards";

  const [batches, setBatches] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [years, setYears] = useState([]);
  const [sources, setSources] = useState({ exams: [], standalone: [], studentCount: 0 });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [filterYear, setFilterYear] = useState("");
  const [showForm, setShowForm] = useState(false);
  const toast = useToast();

  const [form, setForm] = useState({
    name: "",
    academicYear: "",
    classId: "",
    sectionId: "",
    templateId: "",
    columns: [emptyColumn()],
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [yearAll, yearActive, tmpl, classRes] = await Promise.all([
          isPortal ? getAllAcademicYearsPortal({}) : getAllAcademicYears({}),
          isPortal ? getActiveAcademicYearPortal() : getActiveAcademicYear(),
          isPortal
            ? getAllTemplatesPortal({ isActive: "true" })
            : getAllTemplates({ isActive: "true" }),
          isPortal ? getAllClassesForPortal() : getAllClasses(),
        ]);
        if (cancelled) return;
        const names = pickList(yearAll, ["academicYears", "years", "data"])
          .map((y) =>
            typeof y === "string"
              ? y
              : y.yearName || y.name || y.academicYear || ""
          )
          .filter(Boolean);
        const active = resolveYearName(yearActive);
        const set = new Set(names);
        if (active) set.add(active);
        setYears([...set]);
        setFilterYear(active || "");
        setForm((f) => ({ ...f, academicYear: active || names[0] || "" }));
        setTemplates(pickList(tmpl, ["templates", "data"]));
        setClasses(pickList(classRes, ["classes", "data"]));
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isPortal]);

  const loadBatches = useCallback(async () => {
    try {
      setLoading(true);
      const res = isPortal
        ? await getAllBatchesPortal({ academicYear: filterYear || undefined })
        : await getAllBatches({ academicYear: filterYear || undefined });
      setBatches(pickList(res, ["batches", "data"]));
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load batches");
    } finally {
      setLoading(false);
    }
  }, [isPortal, filterYear]);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  const onClassChange = async (classId) => {
    setForm((f) => ({ ...f, classId, sectionId: "" }));
    setSources({ exams: [], standalone: [], studentCount: 0 });
    if (!classId) {
      setSections([]);
      return;
    }
    try {
      const res = isPortal
        ? await getAllSectionsForPortal(classId)
        : await getAllSections(classId);
      setSections(pickList(res, ["sections", "data"]));
    } catch (e) {
      setSections([]);
    }
  };

  useEffect(() => {
    if (!form.classId || !form.sectionId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = isPortal
          ? await getMarksSourcePreviewPortal({
              classId: form.classId,
              sectionId: form.sectionId,
              academicYear: form.academicYear || undefined,
            })
          : await getMarksSourcePreview({
              classId: form.classId,
              sectionId: form.sectionId,
              academicYear: form.academicYear || undefined,
            });
        if (!cancelled)
          setSources({
            exams: res.exams || [],
            standalone: res.standalone || [],
            studentCount: res.studentCount || 0,
            message: res.message,
          });
      } catch (err) {
        if (!cancelled)
          setSources({
            exams: [],
            standalone: [],
            studentCount: 0,
            message: err.response?.data?.message || "Preview failed",
          });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form.classId, form.sectionId, form.academicYear, isPortal]);

  const updateColumn = (idx, patch) => {
    setForm((f) => {
      const columns = [...f.columns];
      columns[idx] = { ...columns[idx], ...patch };
      return { ...f, columns };
    });
  };

  const updateSource = (colIdx, srcIdx, patch) => {
    setForm((f) => {
      const columns = [...f.columns];
      const sources = [...columns[colIdx].sources];
      sources[srcIdx] = { ...sources[srcIdx], ...patch };
      columns[colIdx] = { ...columns[colIdx], sources };
      return { ...f, columns };
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (
      !form.academicYear ||
      !form.classId ||
      !form.sectionId ||
      !form.templateId
    ) {
      toast.error("Year, class, section and template are required");
      return;
    }
    if (!form.columns.length) {
      toast.error("Add at least one column");
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        name: form.name.trim() || undefined,
        academicYear: form.academicYear,
        classId: form.classId,
        sectionId: form.sectionId,
        templateId: form.templateId,
        columns: form.columns.map((c) => ({
          label: c.label,
          fullMarks: Number(c.fullMarks),
          table: c.table,
          optional: Boolean(c.optional),
          sources: c.sources.map((s) => ({
            sourceType: s.sourceType,
            examId: s.sourceType === "EXAM" ? s.examId : undefined,
            standaloneSchemeId:
              s.sourceType === "STANDALONE" ? s.standaloneSchemeId : undefined,
            weightPercent: Number(s.weightPercent) || 100,
          })),
        })),
      };
      const res = isPortal
        ? await createBatchPortal(payload)
        : await createBatch(payload);
      const created = res.batch || res.data;
      toast.success("Batch created");
      if (created?._id) navigate(`${base}/batches/${toId(created)}`);
      else loadBatches();
    } catch (err) {
      toast.error(err.response?.data?.message || "Create failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-primary-50/40 p-4 sm:p-6 lg:p-8 antialiased">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white dark:bg-neutral-900 p-6 rounded-2xl border border-primary-100 shadow-sm">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-primary-600 dark:text-primary-400">
              Report Cards
            </span>
            <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight mt-1">
              Report Batches
            </h1>
            <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400 mt-1 max-w-2xl">
              Pick exam sources, define evaluation matrix columns, and generate total report cards.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              onClick={() => navigate(`${base}/grade-schemes`)}
              className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:bg-neutral-950 dark:hover:bg-neutral-800"
            >
              Grade schemes
            </Button>
            <Button
              type="button"
              onClick={() => navigate(`${base}/templates`)}
              className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:bg-neutral-950 dark:hover:bg-neutral-800"
            >
              Templates
            </Button>
            {canManage && (
              <Button
                type="button"
                onClick={() => setShowForm((v) => !v)}
                className="bg-primary-600 hover:bg-primary-700 text-white shadow-xs"
              >
                <Plus className="h-4 w-4 mr-1.5" /> New batch
              </Button>
            )}
          </div>
        </div>

        {/* Filter Controls Bar */}
        <Card className="p-4 bg-white dark:bg-neutral-900 border-primary-100 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-xs font-semibold text-neutral-700 dark:text-neutral-200">
            <Filter className="h-4 w-4 text-primary-500" />
            <span>Filter Batches</span>
          </div>
          <div className="w-full sm:w-64">
            <select
              value={filterYear || "all"}
              onChange={(e) => setFilterYear(e.target.value === "all" ? "" : e.target.value)}
              className="w-full h-9 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-xs px-3 text-neutral-800 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All academic years</option>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </Card>

        {/* Creation Form Panel */}
        {showForm && canManage && (
          <Card className="border-primary-200 overflow-hidden shadow-md">
            <div className="border-b border-primary-100 bg-primary-50/50 p-6">
              <h2 className="text-base font-bold text-neutral-900 dark:text-neutral-100">
                New Multi-Exam Batch
              </h2>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                Configure target class, evaluation template, and weighted source columns.
              </p>
            </div>
            <div className="p-6">
              <form onSubmit={handleCreate} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="sm:col-span-2 space-y-1.5">
                    <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-200">
                      Batch Name (Optional)
                    </label>
                    <input
                      type="text"
                      className="w-full h-9 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-xs px-3 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="e.g. Annual 2026 Class 5A"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-200">
                      Academic Year *
                    </label>
                    <select
                      className="w-full h-9 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-xs px-3 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      value={form.academicYear}
                      onChange={(e) => setForm({ ...form, academicYear: e.target.value })}
                    >
                      {years.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-200">
                      Class *
                    </label>
                    <select
                      className="w-full h-9 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-xs px-3 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      value={form.classId}
                      onChange={(e) => onClassChange(e.target.value)}
                    >
                      <option value="">Select class</option>
                      {classes.map((c) => (
                        <option key={toId(c)} value={toId(c)}>
                          {c.className || c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-200">
                      Section *
                    </label>
                    <select
                      className="w-full h-9 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-xs px-3 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                      value={form.sectionId}
                      onChange={(e) => setForm({ ...form, sectionId: e.target.value })}
                      disabled={!form.classId}
                    >
                      <option value="">Select section</option>
                      {sections.map((s) => (
                        <option key={toId(s)} value={toId(s)}>
                          {s.sectionName || s.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="sm:col-span-3 space-y-1.5">
                    <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-200">
                      Report Card Template *
                    </label>
                    <select
                      className="w-full h-9 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-xs px-3 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      value={form.templateId}
                      onChange={(e) => setForm({ ...form, templateId: e.target.value })}
                    >
                      <option value="">Select template</option>
                      {templates.map((t) => (
                        <option key={toId(t)} value={toId(t)}>
                          {t.name}
                          {t.gradeSchemeId?.name ? ` · Scheme: ${t.gradeSchemeId.name}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {form.sectionId && (
                  <div className="rounded-xl p-3 bg-primary-50/60 text-xs text-primary-900 dark:text-primary-300 flex flex-wrap items-center gap-2">
                    <span className="bg-primary-100 text-primary-800 dark:text-primary-300 px-2.5 py-1 rounded-md font-semibold">
                      {sources.studentCount} Enrolled Students
                    </span>
                    <span className="bg-primary-100 text-primary-800 dark:text-primary-300 px-2.5 py-1 rounded-md font-semibold">
                      {sources.exams?.length || 0} Exams Available
                    </span>
                    <span className="bg-primary-100 text-primary-800 dark:text-primary-300 px-2.5 py-1 rounded-md font-semibold">
                      {sources.standalone?.length || 0} Standalone Schemes
                    </span>
                    {sources.message && <span className="text-neutral-500 dark:text-neutral-400">— {sources.message}</span>}
                  </div>
                )}

                {/* Dynamic Matrix Columns Configuration */}
                <div className="space-y-4 pt-2">
                  <div className="flex items-center justify-between border-b border-neutral-200 dark:border-neutral-800 pb-2">
                    <div>
                      <h3 className="text-xs font-bold text-neutral-900 dark:text-neutral-100 uppercase tracking-wider">
                        Matrix Columns Configuration
                      </h3>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">Define headers, full marks, and weighting sources</p>
                    </div>
                    <Button
                      type="button"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          columns: [...f.columns, emptyColumn()],
                        }))
                      }
                      className="text-primary-600 dark:text-primary-400 hover:bg-primary-50 border border-primary-200 py-1"
                    >
                      + Add Column
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {form.columns.map((col, ci) => (
                      <div key={ci} className="border border-neutral-200 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-950 rounded-xl p-4 space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
                          <div className="sm:col-span-4 space-y-1">
                            <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-200">
                              Column Label *
                            </label>
                            <input
                              type="text"
                              placeholder="e.g. FA-1, Term-1"
                              className="w-full h-8 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-xs px-2.5 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                              value={col.label}
                              onChange={(e) => updateColumn(ci, { label: e.target.value })}
                              required
                            />
                          </div>
                          <div className="sm:col-span-2 space-y-1">
                            <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-200">
                              Full Marks *
                            </label>
                            <input
                              type="number"
                              min={0}
                              className="w-full h-8 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-xs px-2.5 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                              value={col.fullMarks}
                              onChange={(e) => updateColumn(ci, { fullMarks: e.target.value })}
                              required
                            />
                          </div>
                          <div className="sm:col-span-3 space-y-1">
                            <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-200">
                              Table Section
                            </label>
                            <select
                              className="w-full h-8 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-xs px-2.5 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                              value={col.table}
                              onChange={(e) => updateColumn(ci, { table: e.target.value })}
                            >
                              <option value="MAJOR">Major Table</option>
                              <option value="MINOR">Minor Table</option>
                            </select>
                          </div>
                          <div className="sm:col-span-2 flex items-center gap-2 pb-2">
                            <input
                              type="checkbox"
                              id={`col-opt-${ci}`}
                              className="h-4 w-4 rounded border-slate-300 dark:border-neutral-700 text-primary-600 dark:text-primary-400 focus:ring-primary-500"
                              checked={col.optional}
                              onChange={(e) => updateColumn(ci, { optional: e.target.checked })}
                            />
                            <label htmlFor={`col-opt-${ci}`} className="text-xs text-neutral-700 dark:text-neutral-200 cursor-pointer">
                              Optional
                            </label>
                          </div>
                          <div className="sm:col-span-1 flex justify-end pb-1">
                            <button
                              type="button"
                              className="h-8 w-8 inline-flex items-center justify-center text-neutral-400 dark:text-neutral-500 hover:text-rose-600 transition-colors cursor-pointer"
                              onClick={() =>
                                setForm((f) => ({
                                  ...f,
                                  columns: f.columns.filter((_, j) => j !== ci),
                                }))
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        {/* Sources list inside column */}
                        <div className="space-y-2 pt-2 border-t border-neutral-200 dark:border-neutral-800/80">
                          {col.sources.map((src, si) => (
                            <div key={si} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center bg-white dark:bg-neutral-900 p-2.5 rounded-lg border border-neutral-200 dark:border-neutral-800">
                              <div className="sm:col-span-3">
                                <select
                                  className="w-full h-8 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-xs px-2 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                  value={src.sourceType}
                                  onChange={(e) =>
                                    updateSource(ci, si, {
                                      sourceType: e.target.value,
                                      examId: "",
                                      standaloneSchemeId: "",
                                    })
                                  }
                                >
                                  <option value="EXAM">Exam Source</option>
                                  <option value="STANDALONE">Standalone Scheme</option>
                                </select>
                              </div>

                              <div className="sm:col-span-6">
                                {src.sourceType === "EXAM" ? (
                                  <select
                                    className="w-full h-8 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-xs px-2 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    value={src.examId}
                                    onChange={(e) => updateSource(ci, si, { examId: e.target.value })}
                                  >
                                    <option value="">Select target exam</option>
                                    {(sources.exams || []).map((ex) => (
                                      <option key={ex.examId} value={ex.examId}>
                                        {ex.name} ({ex.assignmentCount} subjects)
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <select
                                    className="w-full h-8 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-xs px-2 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    value={src.standaloneSchemeId}
                                    onChange={(e) => updateSource(ci, si, { standaloneSchemeId: e.target.value })}
                                  >
                                    <option value="">Select scheme</option>
                                    {(sources.standalone || []).map((s) => (
                                      <option key={toId(s.schemeId)} value={toId(s.schemeId)}>
                                        {s.name} (Max: {s.maxMarks})
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </div>

                              <div className="sm:col-span-3 flex items-center gap-1.5">
                                <label className="text-[10px] uppercase text-neutral-400 dark:text-neutral-500 font-bold">Weight %</label>
                                <input
                                  type="number"
                                  min={0}
                                  max={100}
                                  className="w-full h-8 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-xs px-2 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                  value={src.weightPercent}
                                  onChange={(e) => updateSource(ci, si, { weightPercent: e.target.value })}
                                />
                              </div>
                            </div>
                          ))}

                          <button
                            type="button"
                            className="text-xs font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 py-1 cursor-pointer"
                            onClick={() =>
                              updateColumn(ci, {
                                sources: [
                                  ...col.sources,
                                  {
                                    sourceType: "EXAM",
                                    examId: "",
                                    standaloneSchemeId: "",
                                    weightPercent: "100",
                                  },
                                ],
                              })
                            }
                          >
                            + Add Weighted Source
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-neutral-200 dark:border-neutral-800">
                  <Button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:bg-neutral-950 dark:hover:bg-neutral-800"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-primary-600 hover:bg-primary-700 text-white"
                  >
                    {submitting && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
                    Create Batch
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        )}

        {/* Batches Display List */}
        <Card className="overflow-hidden bg-white dark:bg-neutral-900 border-primary-100 shadow-sm">
          <div className="border-b border-neutral-200 dark:border-neutral-800 bg-primary-50/30 px-6 py-4 flex items-center gap-2 text-sm font-bold text-neutral-900 dark:text-neutral-100">
            <Layers className="h-4 w-4 text-primary-600 dark:text-primary-400" />
            Existing Batches
          </div>
          <div>
            {loading ? (
              <div className="p-16 flex flex-col items-center justify-center text-neutral-400 dark:text-neutral-500 gap-2">
                <Loader2 className="h-8 w-8 animate-spin text-primary-600 dark:text-primary-400" />
                <span className="text-xs font-semibold">Loading batch records...</span>
              </div>
            ) : batches.length === 0 ? (
              <div className="p-12 text-center text-xs text-neutral-500 dark:text-neutral-400">
                No batches found for the selected academic year.
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-neutral-800">
                {batches.map((b) => (
                  <div
                    key={toId(b)}
                    onClick={() => navigate(`${base}/batches/${toId(b)}`)}
                    className="p-5 hover:bg-primary-50/20 transition-colors cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                  >
                    <div className="space-y-1">
                      <div className="font-bold text-neutral-900 dark:text-neutral-100 text-sm sm:text-base">
                        {b.name || (b.examNames || []).join(" + ") || "Report Batch"} · Class {b.className || b.classId?.className} - {b.sectionName || b.sectionId?.sectionName}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400">
                        <Badge className="bg-primary-50 text-primary-700 dark:text-primary-400 border border-primary-200">
                          {b.academicYear}
                        </Badge>
                        <span>• {b.studentCount || 0} Students</span>
                        <span>• {(b.columns || []).length} Columns</span>
                        <span>• Template: <strong className="text-neutral-700 dark:text-neutral-200">{b.templateName || b.templateId?.name || "Template"}</strong></span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <BatchStatusBadge status={b.status} />
                      <ChevronRight className="h-4 w-4 text-neutral-300 dark:text-neutral-600" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default BatchList;